/**
 * Paths that can only be viewed from the listed countries.
 * The country is detected client-side through the MaxMind ipService
 * (config.api.country.ipService) via fetchCurrentCountry().
 * Country codes are ISO 3166-1 alpha-2.
 */
export const GEO_BLOCKED_PATHS = [
  { path: "/en-vivo/radio-nacional", allowedCountries: ["CO"] }
];

/**
 * Check if a pathname has a geo-blocking rule defined.
 *
 * @param {string} pathname - current location pathname
 * @returns {object|undefined} the matching rule, if any
 */
export function getGeoRule(pathname) {
  if (!pathname) {
    return undefined;
  }
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  return GEO_BLOCKED_PATHS.find(({ path }) => path === normalizedPath);
}

/**
 * Check if the country in the store is a valid detected value.
 *
 * @param {string|object} country - country value from the store
 * @returns {boolean}
 */
export function isCountryDetected(country) {
  return typeof country === "string" && country.length === 2;
}

/**
 * Check if a path must be geo-blocked for the detected country.
 * Fails open: if the country could not be detected, the content is shown.
 *
 * @param {string} pathname - current location pathname
 * @param {string|object} country - detected country code from the store
 * @returns {boolean}
 */
export function isGeoBlockedPath(pathname, country) {
  const rule = getGeoRule(pathname);
  if (!rule || !isCountryDetected(country)) {
    return false;
  }
  return !rule.allowedCountries.includes(country.toUpperCase());
}

/**
 * Check if a content item must be geo-blocked for the detected country,
 * based on the content's own `geoblocking` field (the list of countries
 * where it is allowed). Country comes from the redux store
 * (`state.country.country`, populated from the cloudfront-viewer-country
 * header at SSR).
 *
 * Fails open: if `geoblocking` is empty/null the content is worldwide, and
 * if the country could not be detected the content is shown.
 *
 * @param {Array<{value:string}>|Array<string>} geoblocking - allowed countries
 * @param {string} country - detected country code from the store
 * @returns {boolean} true when the content must be blocked
 */
export function isContentGeoBlocked(geoblocking, country) {
  if (!Array.isArray(geoblocking) || geoblocking.length === 0) {
    return false;
  }
  if (!isCountryDetected(country)) {
    return false;
  }
  const iso = country.toLowerCase();
  return !geoblocking.some(rule => {
    const value = rule && typeof rule === "object" ? rule.value : rule;
    return value != null && value.toString().toLowerCase() === iso;
  });
}
